import { EPenaltyStatus } from "@/constants/enums/penalty-reason.enum";

export interface PenaltyItem {
  month?: string;
  penaltyAmount?: number | null;
  outstandingAmount?: number | null;
  paidAmount?: number | null;
  employeeCount?: number | null;
  status: EPenaltyStatus;
  calculation?: {
    contributionPeriodEnd?: string | Date;
    daysInDefault?: number | null;
  };
}

export interface MonthlyPenalty {
  year: number;
  /** Period key as returned by the API, e.g. `"2026-01"` (may be empty in fallback cases). */
  month: string;
  /** Localized month name for the table, e.g. `"January"`. */
  monthLabel: string;
  amount: number;
  /** Total paid for the period, taken directly from the endpoint (`null` when absent). */
  paidAmount: number | null;
  /**
   * Outstanding for the period, taken directly from the endpoint's `outstandingAmount`.
   * Sourced from the dedicated field — never derived from the (frozen) per-month penalty.
   */
  outstandingAmount: number | null;
  employeeCount: number | null;
  daysInDefault: number | null;
}

export interface YearlyPenalty {
  year: number;
  total: number;
  months: MonthlyPenalty[];
}

/** Live page-level penalty totals carried on `GET compliance/penalties` → `summary`. */
export interface PenaltySummary {
  totalPenalty?: number | null;
  totalOutstanding?: number | null;
}

type MonthAgg = {
  amount: number;
  paidAmount: number | null;
  outstandingAmount: number | null;
  employeeCount: number | null;
  daysInDefault: number | null;
};

const MONTH_ORDER = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const isYearMonthPeriod = (value: string) => /^\d{4}-\d{2}$/.test(value);

const parseEmployeeCountFromItem = (item: PenaltyItem): number | null => {
  const raw = item.employeeCount;
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const parseDaysInDefaultFromItem = (item: PenaltyItem): number | null => {
  const raw = item.calculation?.daysInDefault;
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const mergeNullableMax = (
  existing: number | null,
  incoming: number | null
): number | null => {
  if (incoming === null) return existing;
  if (existing === null) return incoming;
  return Math.max(existing, incoming);
};

const parseNullableAmount = (raw: number | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/** Sum that stays `null` until at least one non-null value is seen. */
const sumNullable = (
  existing: number | null,
  incoming: number | null
): number | null => {
  if (incoming === null) return existing;
  return (existing ?? 0) + incoming;
};

/**
 * Pull the live `summary` off the penalties payload, tolerating both envelope
 * shapes seen in the wild (`{ summary }` and `{ data: { summary } }`).
 */
export const getPenaltySummary = (penalties: unknown): PenaltySummary | null => {
  if (!penalties || typeof penalties !== "object") return null;
  const obj = penalties as {
    summary?: unknown;
    data?: { summary?: unknown } | null;
  };
  const summary = obj.summary ?? obj.data?.summary;
  return summary && typeof summary === "object"
    ? (summary as PenaltySummary)
    : null;
};

const unwrapPenalties = (penalties: unknown): PenaltyItem[] => {
  if (Array.isArray(penalties)) return penalties as PenaltyItem[];

  const maybeWrapper = penalties as unknown as { data?: unknown };
  const topLevelData = maybeWrapper.data;

  if (Array.isArray(topLevelData)) return topLevelData as PenaltyItem[];

  if (
    topLevelData &&
    typeof topLevelData === "object" &&
    "data" in topLevelData
  ) {
    const nested = topLevelData as { data?: unknown };
    if (Array.isArray(nested.data)) return nested.data as PenaltyItem[];
  }

  return [];
};

export const buildPenaltyBreakdown = (penalties: unknown): YearlyPenalty[] => {
  const rawPenalties = unwrapPenalties(penalties);
  if (!rawPenalties.length) return [];

  const byYear = new Map<number, Map<string, MonthAgg>>();

  rawPenalties.forEach((item: PenaltyItem) => {
    if (!item) return;
    if (item.status === EPenaltyStatus.GAP_DETECTED) return;
    const rawMonth: string | undefined = item.month;

    // penaltyAmount/outstandingAmount may come back as null; still render the month row.
    const amount = Number(
      item.penaltyAmount ?? item.outstandingAmount ?? item.paidAmount ?? 0
    );

    if (!rawMonth) return;

    const monthLabel = isYearMonthPeriod(rawMonth)
      ? new Date(`${rawMonth}-01`).toLocaleDateString("en-US", {
          month: "long",
        })
      : rawMonth;

    const periodEnd = item.calculation?.contributionPeriodEnd;
    // Prefer year from `YYYY-MM` so it stays aligned with `month` (e.g. "2026-01" → 2026).
    const year = isYearMonthPeriod(rawMonth)
      ? Number(rawMonth.split("-")[0])
      : periodEnd
      ? new Date(periodEnd).getFullYear()
      : new Date().getFullYear();

    // Aggregate by API period key `YYYY-MM` when present (e.g. "2026-01").
    const periodKey = isYearMonthPeriod(rawMonth)
      ? rawMonth
      : `${year}__${monthLabel}`;

    if (!byYear.has(year)) {
      byYear.set(year, new Map<string, MonthAgg>());
    }
    const monthMap = byYear.get(year)!;
    const prev: MonthAgg = monthMap.get(periodKey) ?? {
      amount: 0,
      paidAmount: null,
      outstandingAmount: null,
      employeeCount: null,
      daysInDefault: null,
    };

    const rowEmployeeCount = parseEmployeeCountFromItem(item);
    const rowDaysInDefault = parseDaysInDefaultFromItem(item);
    monthMap.set(periodKey, {
      amount: prev.amount + (Number.isFinite(amount) ? amount : 0),
      // Paid/Outstanding come straight from the endpoint's dedicated fields.
      paidAmount: sumNullable(prev.paidAmount, parseNullableAmount(item.paidAmount)),
      outstandingAmount: sumNullable(
        prev.outstandingAmount,
        parseNullableAmount(item.outstandingAmount)
      ),
      employeeCount: mergeNullableMax(prev.employeeCount, rowEmployeeCount),
      daysInDefault: mergeNullableMax(prev.daysInDefault, rowDaysInDefault),
    });
  });

  const yearly = Array.from(byYear.entries())
    .map(([year, monthMap]) => {
      const months = Array.from(monthMap.entries())
        .filter(([, agg]) => agg.amount > 0)
        .sort(([a], [b]) => {
          if (isYearMonthPeriod(a) && isYearMonthPeriod(b)) {
            return a.localeCompare(b);
          }
          if (isYearMonthPeriod(a)) return -1;
          if (isYearMonthPeriod(b)) return 1;

          const labelA = a.includes("__") ? a.split("__")[1] ?? "" : a;
          const labelB = b.includes("__") ? b.split("__")[1] ?? "" : b;

          return MONTH_ORDER.indexOf(labelA) - MONTH_ORDER.indexOf(labelB);
        })
        .map(([periodKey, agg]) => {
          const monthLabel = isYearMonthPeriod(periodKey)
            ? new Date(`${periodKey}-01`).toLocaleDateString("en-US", {
                month: "long",
              })
            : periodKey.split("__")[1] ?? periodKey;

          return {
            year,
            // API period `YYYY-MM` (e.g. `"2026-01"`); empty if month was not in that shape.
            month: isYearMonthPeriod(periodKey) ? periodKey : "",
            monthLabel,
            amount: agg.amount,
            paidAmount: agg.paidAmount,
            outstandingAmount: agg.outstandingAmount,
            employeeCount: agg.employeeCount,
            daysInDefault: agg.daysInDefault,
          };
        });

      // Year total = sum of each month's outstanding amount (what is still owed).
      const total = months.reduce(
        (sum, m) => sum + (m.outstandingAmount ?? 0),
        0
      );
      return { year, total, months };
    })
    .filter((y) => y.months.length > 0);

  return yearly.sort((a, b) => a.year - b.year);
};
